<section class="migrate-page">

  <!-- HERO -->
  <header class="migrate-hero">
    <div class="migrate-hero__content">
      <div class="migrate-hero__kicker">Migrating from Heroku</div>
      <h1 class="migrate-hero__title">Migrate from Heroku to EU infrastructure</h1>
      <p class="migrate-hero__subtitle">
        Most Heroku apps migrate to Partiri in minutes. We support the same Git-based deployment
        model and the runtimes you already use, so your existing app will likely work without
        code changes. Your infrastructure runs entirely within the EU, GDPR-compliant by default.
      </p>
      <div class="migrate-hero__cta">
        <a mat-flat-button routerLink="/register" class="btn-primary">
          <mat-icon>rocket_launch</mat-icon>
          Deploy your first app
        </a>
        <a mat-outlined-button routerLink="/documentation/start" class="btn-ghost">
          <mat-icon>description</mat-icon>
          Read the deploy guide
        </a>
      </div>
    </div>
  </header>

  <!-- WHAT MAPS TO WHAT -->
  <section class="migrate-section" id="mapping">
    <div class="migrate-section__head">
      <div class="migrate-section__kicker">What maps to what</div>
      <h2 class="migrate-section__title">Heroku concepts, translated</h2>
      <p class="migrate-section__subtitle">
        Environment variables, add-on credentials, and custom domains all map directly to Partiri
        equivalents.
      </p>
    </div>

    <table class="migrate-table">
      <thead>
        <tr>
          <th>On Heroku</th>
          <th>On Partiri</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Procfile</td>
          <td>A run command set in the service settings</td>
        </tr>
        <tr>
          <td>Buildpacks</td>
          <td>Not supported — use a native runtime, detected automatically from files like package.json or requirements.txt, or bring a Dockerfile</td>
        </tr>
        <tr>
          <td>Config Vars</td>
          <td>Environment variables, injected at runtime and editable from the service settings page</td>
        </tr>
        <tr>
          <td>Dynos</td>
          <td>Pods, sized Nano through Big depending on the CPU and memory your service needs</td>
        </tr>
        <tr>
          <td>Add-on credentials (Heroku Postgres, Redis, etc.)</td>
          <td>Set the connection string as an environment variable; for file-based data such as SQLite, use Persistent Storage instead</td>
        </tr>
        <tr>
          <td>Heroku Scheduler</td>
          <td>The Cron Job service type</td>
        </tr>
        <tr>
          <td>Review apps</td>
          <td>Not supported — see limitations below</td>
        </tr>
        <tr>
          <td>Custom domains &amp; SSL</td>
          <td>Custom domains with TLS certificates provisioned automatically</td>
        </tr>
        <tr>
          <td>git push heroku main</td>
          <td>A Git push webhook to your configured branch, or a manual deploy from the dashboard</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- LIMITATIONS -->
  <section class="migrate-section" id="limitations">
    <div class="migrate-section__head">
      <div class="migrate-section__kicker">Honest limitations</div>
      <h2 class="migrate-section__title">What doesn't carry over</h2>
      <p class="migrate-section__subtitle">
        We'd rather tell you upfront than have you find out mid-migration.
      </p>
    </div>

    <ul class="migrate-limitations">
      <li>
        <mat-icon>info</mat-icon>
        <p><strong>No buildpacks.</strong> Heroku-style buildpacks are not supported. Use a native
        runtime — we detect Node.js, Python, Go, Rust, Ruby, Elixir, PHP, JVM, .NET, C++ and static
        sites automatically — or bring a Dockerfile, which works unchanged.</p>
      </li>
      <li>
        <mat-icon>info</mat-icon>
        <p><strong>No review apps.</strong> There is no automatic preview environment feature for
        pull requests. Deployments are triggered manually or via Git push webhooks to a configured
        branch.</p>
      </li>
      <li>
        <mat-icon>info</mat-icon>
        <p><strong>Managed PostgreSQL, no Redis.</strong> Partiri offers Managed PostgreSQL
        databases (single-instance, EU-hosted, private in-cluster networking) — there's no
        backups, point-in-time recovery, high availability, or connection pooling in v1, and no
        managed Redis. Persistent Storage attaches a volume (1–10 GB) to a service for file
        uploads, SQLite databases, or caches; for anything needing those guarantees, keep it
        external and point your service at it with an environment variable.</p>
      </li>
    </ul>
  </section>

  <!-- FIRST DEPLOY -->
  <section class="migrate-section" id="first-deploy">
    <div class="migrate-section__head">
      <div class="migrate-section__kicker">Getting started</div>
      <h2 class="migrate-section__title">Your first deploy, step by step</h2>
      <p class="migrate-section__subtitle">
        Replace your Procfile with a run command and you're most of the way there.
      </p>
    </div>

    <ol class="migrate-steps">
      <li>
        <span class="migrate-steps__num">1</span>
        <div>
          <h3 class="migrate-steps__title">Connect your source</h3>
          <p class="migrate-steps__desc">
            Point Partiri at your Git repository (GitHub, GitLab, Bitbucket, or Codeberg) or a
            container image, the same repository you already deploy to Heroku from.
          </p>
        </div>
      </li>
      <li>
        <span class="migrate-steps__num">2</span>
        <div>
          <h3 class="migrate-steps__title">Set your run command</h3>
          <p class="migrate-steps__desc">
            Replace your Procfile's <code>web:</code> line with a run command in the service
            settings, or add a Dockerfile if you'd rather build the image yourself.
          </p>
        </div>
      </li>
      <li>
        <span class="migrate-steps__num">3</span>
        <div>
          <h3 class="migrate-steps__title">Carry over your Config Vars</h3>
          <p class="migrate-steps__desc">
            Copy your Config Vars into environment variables, and swap any add-on connection
            strings for the credentials of the external service you keep using.
          </p>
        </div>
      </li>
      <li>
        <span class="migrate-steps__num">4</span>
        <div>
          <h3 class="migrate-steps__title">Pick a region and pod size</h3>
          <p class="migrate-steps__desc">
            Choose a European region and a pod size — all available regions are based in Europe.
          </p>
        </div>
      </li>
      <li>
        <span class="migrate-steps__num">5</span>
        <div>
          <h3 class="migrate-steps__title">Deploy</h3>
          <p class="migrate-steps__desc">
            We build your app, assign it a subdomain, and it's live. Attach your custom domain
            whenever you're ready.
          </p>
        </div>
      </li>
    </ol>

    <a routerLink="/documentation/start" class="link-btn">
      Read the full deployment guide
      <mat-icon>arrow_forward</mat-icon>
    </a>
  </section>

  <!-- EU RESIDENCY -->
  <section class="migrate-section" id="eu-residency">
    <div class="migrate-section__head">
      <div class="migrate-section__kicker">Data residency</div>
      <h2 class="migrate-section__title">Infrastructure that stays in the EU</h2>
    </div>

    <div class="migrate-residency">
      <p>
        Partiri is a European company, and our infrastructure runs entirely out of European
        regions. Every deployment — build, storage, and traffic — stays within the EU, and we're
        GDPR-compliant by default with no extra configuration needed.
      </p>
      <p>
        Regions are live today in Germany (Nuremberg) and Finland (Helsinki), with more European
        countries on the roadmap.
      </p>
      <a routerLink="/roadmap" class="link-btn">
        See where we're live
        <mat-icon>arrow_forward</mat-icon>
      </a>
    </div>
  </section>

  <!-- CTA -->
  <section class="migrate-section">
    <div class="migrate-cta-banner">
      <h2 class="migrate-cta-banner__title">Ready to leave Heroku behind?</h2>
      <p class="migrate-cta-banner__subtitle">
        Bring your Procfile-free app, your Config Vars, and deploy on EU-only infrastructure.
      </p>
      <a mat-flat-button routerLink="/register" class="btn-primary btn-primary--lg">
        Create your project
      </a>
    </div>
  </section>

</section>
